import type { CardData, GrandSnatchState } from './types';
import type { EffectTrigger } from './effects';
import { EFFECT_REGISTRY, type EffectPayload, type EffectContext } from './effect_registry';

// Owner lookup: cards have IDs prefixed `p0_*` or `p1_*`. We use that to
// find which player owns the carrier card without a separate index.
function ownerOf(card: CardData): '0' | '1' {
  return card.id.startsWith('p0') ? '0' : '1';
}

// Run a single card's effects matching `trigger`, in array order.
function runCardEffects(
  G: GrandSnatchState,
  card: CardData,
  trigger: EffectTrigger,
  payload: EffectPayload
): void {
  if (!card.effects) return;
  const owner = ownerOf(card);
  const opponent = owner === '0' ? '1' : '0';
  const ctx: EffectContext = { G, playerID: owner, opponentID: opponent, payload };
  for (const eff of card.effects) {
    // `trigger` may be a single value or an array (stateful effects that react
    // to several lifecycle moments while sharing one `state`).
    const triggers = Array.isArray(eff.trigger) ? eff.trigger : [eff.trigger];
    if (!triggers.includes(trigger)) continue;
    const apply = EFFECT_REGISTRY[eff.kind];
    if (apply) apply(ctx, eff, card);
  }
}

// Single dispatch entry point. Routes each trigger to the right scan-set so
// we don't waste cycles iterating cards that can't react.
export function dispatch(G: GrandSnatchState, payload: EffectPayload): void {
  const trigger = payload.trigger;
  switch (payload.trigger) {
    case 'on_play':
    case 'on_table':
    case 'on_snatch':
    case 'on_draw':
      runCardEffects(G, payload.card, trigger, payload);
      return;

    case 'on_capture':
      runCardEffects(G, payload.taker, trigger, payload);
      return;

    case 'on_captured':
      runCardEffects(G, payload.victim, trigger, payload);
      return;

    case 'on_resource_gained': {
      // Broadcast: every card in either player's discard, dropzones (cards +
      // missionCards) gets a chance to react. Bounded set; OK to scan.
      for (const pid of ['0', '1'] as const) {
        for (const c of G.players[pid].discard) {
          runCardEffects(G, c, trigger, payload);
        }
      }
      for (const dz of G.dropzones) {
        for (const c of dz.cards) runCardEffects(G, c, trigger, payload);
        for (const c of dz.missionCards) runCardEffects(G, c, trigger, payload);
      }
      return;
    }

    case 'on_game_start': {
      // Once at draft.onEnd. Walk both decks (every card lives there at this
      // moment — hands have already been drawn AFTER this dispatch site).
      for (const pid of ['0', '1'] as const) {
        for (const c of G.players[pid].deck) {
          runCardEffects(G, c, trigger, payload);
        }
        for (const c of G.players[pid].hand) {
          runCardEffects(G, c, trigger, payload);
        }
      }
      return;
    }

    case 'on_round_end': {
      // Round boundary tick (da Game.ts stage.onEnd). Scansiona le carte che
      // possono ospitare un effetto stateful tra un round e l'altro: le mani
      // (held_charge) e le carte sulle dropzone (ripening_loot, time_bomb,
      // decaying_value). Le dropzone si iterano su snapshot perché time_bomb
      // può riassegnare dz.cards durante la scansione.
      // Anche i mazzi (= mano 2 in attesa): servono a held_charge con
      // countDeck (Syndicate High Rollers). Gli altri handler on_round_end
      // controllano da soli di essere su una dropzone, quindi è innocuo.
      for (const pid of ['0', '1'] as const) {
        for (const c of G.players[pid].hand) {
          runCardEffects(G, c, trigger, payload);
        }
        for (const c of G.players[pid].deck) {
          runCardEffects(G, c, trigger, payload);
        }
      }
      for (const dz of G.dropzones) {
        for (const c of [...dz.cards]) runCardEffects(G, c, trigger, payload);
      }
      return;
    }

    case 'in_discard':
      // Never invoked through dispatch — read declaratively by evaluateScoringRule.
      return;
  }
}
